<template>
    <md-layout md-flex="100" md-column class="ql-pages-container">
        <md-layout md-row md-gutter="12" class="ql-page-layout" >
            <svg class="ql-page-graphics" xmlns="http://www.w3.org/2000/svg" version="1.1" 
                :height="svgTrueHeight" :style="{height: `${svgTrueHeight}px`}" v-if="pageReady">
                <!-- SVG lines connecting cues with cue markers. Y + 1 so it lines up nicely -->
                <line v-for="cue in positionedCues" 
                    :x1="cue.offsetX + cue.width" :y1="cuePosTop(cue)" 
                    :x2="svgOffsetEnd" :y2="cue.offsetY"
                    :stroke="cueColor(cue)" stroke-width="1"/>
            </svg>
            <md-layout md-flex="70">
                <project-page v-for="(page, index) in pages" 
                    v-if="current(index + 1)"
                    :page="page"
                    :cues="visibleCues(index + 1)"
                    :key="index" 
                    :layers="visibleLayers"
                    :currentLayer="currentLayer"
                    :locked="locked"
                    :showMode="showMode"
                    @pageLoaded="pageLoaded"
                    @addedCue="addedCue"
                    @deletedCue="deletedCue"
                    @updatedCue="updatedCue"/>
            </md-layout>
            <md-layout md-flex="30">
                <project-cue-list 
                    :cues="positionedCues" 
                    :layers="visibleLayers" 
                    :locked="locked"
                    :openCues="openCues"
                    @openCue="openCue"
                    @closeCue="closeCue"
                    @destroy="deletedCue"
                    @updatedCue="updatedCue"/>
            </md-layout> 
        </md-layout>
    </md-layout>
</template>

<script>
import { mapGetters } from 'vuex';
import vueDragResize from 'vue-draggable-resizable';

import ProjectPage from '@/components/project/page';
import ProjectCueList from '@/components/project/cues/cue-list';

const ProjectPages = {
    name: 'projectPages',
    props: [
        'currentPage',
        'currentUser',
        'pages',
        'locked',
        'showMode'
    ],
    data: function() {
        return {
            svgOffsetStart: 0,
            svgOffsetEnd: 0,
            cueHeight: 48,
            cueOpenHeight: 180,
            cueBottomPadding: 10,
            svgHeight: 100,
            svgContentWidth: 100,
            pageReady: false,
            openCues: []
        };
    },
    computed: {
        ...mapGetters([
            'project',
            'layerByCue',
            'visibleCues',
            'visibleLayers',
            'currentLayer',
            'layers'
        ]),
        positionedCues: function() {
            const allCues = this.visibleCues(this.currentPage);
            allCues.sort((a, b) => {
                if (a.y === b.y) {
                    return a.x - b.x;
                } 

                return a.y - b.y;
            });
            const positionedCues = [];

            for (let i = 0; i < allCues.length; i++) {
                const cue = JSON.parse(JSON.stringify(allCues[i]));

                cue.currentHeight = this.getCueHeight(cue);
                if (cue.y <= 1 && cue.x <= 1) {
                    cue.offsetY = cue.y * this.svgHeight;
                    cue.offsetX = (cue.x * this.svgContentWidth) + this.svgOffsetStart;
                } else {
                    cue.offsetY = cue.y;
                    cue.offsetX = cue.x + this.svgOffsetStart;
                }
                // console.log(cue);

                if (positionedCues.length > 0) {
                    const otherCue = positionedCues[positionedCues.length - 1];
                    if (cue.offsetY < otherCue.offsetY + otherCue.currentHeight) {
                        cue.offsetY = otherCue.offsetY + otherCue.currentHeight + this.cueBottomPadding;
                    }
                }

                positionedCues.push(cue);
            }

            return positionedCues;
        },
        svgTrueHeight: function() {
            const lastElement = this.positionedCues[this.positionedCues.length - 1];
            if (lastElement) {
                return Math.max(this.svgHeight, lastElement.offsetY);
            }

            return this.svgHeight;
        }
    },
    methods: {
        cueColor(cue) {
            const layer = this.layerByCue(cue);
            if (layer) return layer.color;

            return 'black';
        },
        getCueHeight(cue) {
            const cueOpenSet = new Set(this.openCues);
            const tempHeight = cueOpenSet.has(cue.id) ? this.cueOpenHeight : this.cueHeight;
            return tempHeight + this.cueBottomPadding;
        },
        cuePosTop(cue) {
            if (cue.x <= 1 && cue.y <= 1) {
                return (cue.y * this.svgHeight) + 1;
            }
            return cue.y + 1;
        },
        inRange(key) {
            return key > (this.$props.currentPage - 2) && key < (this.$props.currentPage + 2);
        },
        current(key) {
            return this.$props.currentPage === key;
        },
        openCue(cue) {
            this.openCues.push(cue.id);
        },
        closeCue(cue) {
            const cueIndex = this.openCues.findIndex(c => c === cue.id);
            this.openCues.splice(cueIndex, 1);
        },
        addedCue: function(cue) {
            this.$store.dispatch('addCue', cue);
        },
        deletedCue: function(cue) {
            this.$store.dispatch('deleteCue', cue);
        },
        updatedCue: function(cue) {
            this.$store.dispatch('updateCue', cue);
        },
        pageLoaded(data) {
            console.log(data);
            this.svgHeight = data.pageHeight;
            this.svgOffsetStart = data.offsetX;
            this.svgContentWidth = data.pageWidth;
            this.svgOffsetEnd = data.width + (data.offsetX * 2) + 15;
            this.pageReady = true;
        },

        sortCues(cues) { 
            return cues.sort((a, b) => {
                if (a.y === b.y) {
                    if (a.x === b.x) {
                        return a.id > b.id;
                    }
                    return a.x > b.x;
                } 

                return a.y > b.y;
            });
        },
        getCueRect(cue) {
            return {
                left: 0, 
                top: cue.offsetY, 
                right: 10, 
                bottom: cue.offsetY + cue.currentHeight
            };
        },
        intersectRect(r1, r2) {
            return !(r2.left > r1.right || 
                   r2.right < r1.left || 
                   r2.top > r1.bottom ||
                   r2.bottom < r1.top);
        }
    },
    components: {
        'project-page': ProjectPage,
        'project-cue-list': ProjectCueList,
        'vue-draggable-resizable': vueDragResize
    },
    mounted: function() {}
};

export default ProjectPages;
</script>

<style>
.ql-page-container {
    padding: .5em 0 0 0 ;
}

.ql-page-graphics {
    position: absolute;
    z-index: 10;
    width: 100%;
    pointer-events: none;
}

.ql-page-graphics .resizeable {
    pointer-events: stroke;
    cursor: nwse-resize;
}

.ql-page-graphics .moveable {
    pointer-events: fill;
    cursor: move;
}

</style>


// WEBPACK FOOTER //
// pages.vue?20897f1a